import { ROLES } from '../constants/roles';

export const HOUSE_BROADCAST_PREFIX = 'house:';
export const HOUSE_BROADCAST_TITLE_MAX = 120;
export const HOUSE_BROADCAST_BODY_MAX = 2000;

/** House members who receive a house-wide in-app + push broadcast. */
export const HOUSE_BROADCAST_AUDIENCE_ROLES = [
  ROLES.GOD,
  ROLES.BANKER,
  ROLES.LISTER,
  ROLES.RUNNER,
  ROLES.SUPPORT_AGENT,
] as const;

export type HouseBroadcastDoc = {
  id: string;
  houseId: string;
  title: string;
  body: string;
  fromUid: string;
  fromName: string;
  fromRole: string;
  publishedAtMs: number;
};

export function canSendHouseBroadcast(role: string | null | undefined): boolean {
  return role === ROLES.GOD || role === ROLES.BANKER;
}

export function houseBroadcastRecipientId(houseId: string): string {
  return `${HOUSE_BROADCAST_PREFIX}${houseId}`;
}

export function parseHouseBroadcastHouseId(id: string | null | undefined): string | null {
  if (!id || !id.startsWith(HOUSE_BROADCAST_PREFIX)) return null;
  const houseId = id.slice(HOUSE_BROADCAST_PREFIX.length).trim();
  return houseId || null;
}

export function isHouseBroadcastRecipientId(id: string | null | undefined): boolean {
  return parseHouseBroadcastHouseId(id) != null;
}

export function canSendHouseBroadcastToHouse(
  role: string | null | undefined,
  houseId: string,
  managedHouseIds: string[],
): boolean {
  if (!canSendHouseBroadcast(role) || !houseId.trim()) return false;
  if (role === ROLES.GOD) return true;
  return managedHouseIds.includes(houseId);
}

export function validateHouseBroadcastCopy(title: string, body: string): string | null {
  const nextTitle = title.trim();
  const nextBody = body.trim();
  if (!nextTitle || !nextBody) return 'title and body are required';
  if (nextTitle.length > HOUSE_BROADCAST_TITLE_MAX) return 'title too long';
  if (nextBody.length > HOUSE_BROADCAST_BODY_MAX) return 'body too long';
  return null;
}

export function houseBroadcastInboxId(houseId: string, broadcastId: string): string {
  return `broadcast:${houseId}:${broadcastId}`;
}

export function listHouseBroadcastTargets(
  role: string | null | undefined,
  houses: Array<{ id?: string; name?: string }>,
  managedHouseIds: string[],
): Array<{ id: string; houseId: string; name: string }> {
  if (!canSendHouseBroadcast(role)) return [];
  const seen = new Set<string>();
  const next: Array<{ id: string; houseId: string; name: string }> = [];
  for (const house of houses) {
    const houseId = String(house.id ?? '').trim();
    if (!houseId || seen.has(houseId)) continue;
    if (!canSendHouseBroadcastToHouse(role, houseId, managedHouseIds)) continue;
    seen.add(houseId);
    next.push({
      id: houseBroadcastRecipientId(houseId),
      houseId,
      name: String(house.name ?? '').trim() || houseId,
    });
  }
  return next;
}
